import { generateUUID } from '@business/utils/uuid';
const EXPIRATION_MINUTES = 10;
const ROUTER_PATHNAME_KEY = 'routerPathname';
class PersistentPropertyStorage {
    constructor() {
        Object.defineProperty(this, "propertyId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: ''
        });
    }
    /**
     * If the current blob is expired, removes it and returns true (session was reset).
     * Does not use read-through helpers so we can surface UX before any load().
     */
    checkExpiredAndClear() {
        var _a;
        if (!this.propertyId) {
            return false;
        }
        const raw = sessionStorage.getItem(this.propertyId);
        if (!raw) {
            return false;
        }
        let data;
        try {
            data = JSON.parse(raw);
        }
        catch (_b) {
            return false;
        }
        const expMs = parseInt(String((_a = data._expiration) !== null && _a !== void 0 ? _a : ''), 10);
        if (Number.isFinite(expMs) && expMs < Date.now()) {
            sessionStorage.removeItem(this.propertyId);
            return true;
        }
        return false;
    }
    clear() {
        if (!this.propertyId)
            return;
        sessionStorage.removeItem(this.propertyId);
    }
    getItem(key) {
        const data = this.readDataRespectingExpiration();
        const value = data[key];
        if (value === undefined || value === null) {
            return null;
        }
        return typeof value === 'string' ? value : String(value);
    }
    getRouterPathname() {
        return this.getItem(ROUTER_PATHNAME_KEY);
    }
    getSearchParams() {
        let str = this.getItem('_searchParams') || '';
        if (str.length > 0 && str[0] === '?') {
            str = str.slice(1);
        }
        const searchParams = new URLSearchParams(str);
        const res = {};
        searchParams.forEach((value, key) => {
            res[key] = value;
        });
        return res;
    }
    init(propertyId) {
        this.propertyId = propertyId.toString();
    }
    get isInitialized() {
        return !!this.propertyId;
    }
    get sessionId() {
        if (!this.isInitialized)
            return '';
        // if the id is not defined, generate a new one
        return (this.getItem('sessionId') || this.setItem('sessionId', generateUUID()));
    }
    readDataRespectingExpiration() {
        var _a;
        if (!this.propertyId) {
            return {};
        }
        const raw = sessionStorage.getItem(this.propertyId);
        if (!raw) {
            return {};
        }
        let data;
        try {
            data = JSON.parse(raw);
        }
        catch (_b) {
            return {};
        }
        const now = Date.now();
        const expMs = parseInt(String((_a = data._expiration) !== null && _a !== void 0 ? _a : ''), 10);
        if (Number.isFinite(expMs) && expMs < now) {
            sessionStorage.removeItem(this.propertyId);
            return {};
        }
        return data;
    }
    setItem(key, value) {
        const data = Object.assign(Object.assign({}, this.readDataRespectingExpiration()), { [key]: value });
        // update expiration
        data._expiration = (Date.now() + 1000 * 60 * EXPIRATION_MINUTES).toString();
        sessionStorage.setItem(this.propertyId, JSON.stringify(data));
        return value;
    }
    setRouterPathname(pathname) {
        this.setItem(ROUTER_PATHNAME_KEY, pathname);
    }
    setSearchParams(searchParams) {
        this.setItem('_searchParams', searchParams);
    }
    /**
     * Extends the sliding expiration by reading the stored blob, updating its
     * expiration timestamp, and writing the updated blob back to sessionStorage.
     */
    touch() {
        var _a;
        if (!this.propertyId) {
            return;
        }
        const raw = sessionStorage.getItem(this.propertyId);
        if (!raw) {
            return;
        }
        let data;
        try {
            data = JSON.parse(raw);
        }
        catch (_b) {
            return;
        }
        const expMs = parseInt(String((_a = data._expiration) !== null && _a !== void 0 ? _a : ''), 10);
        if (Number.isFinite(expMs) && expMs < Date.now()) {
            sessionStorage.removeItem(this.propertyId);
            return;
        }
        data._expiration = (Date.now() + 1000 * 60 * EXPIRATION_MINUTES).toString();
        sessionStorage.setItem(this.propertyId, JSON.stringify(data));
    }
}
export const persistentPropertyStorage = new PersistentPropertyStorage();
